/*------------------------------------------------------------------------------
   About      : Feature picker for custom access groups — two categories

   Mirrors the Assignments split (design doc §1.4): "API Permissions" lists
   backend-discovered features (ui.* rows excluded), "Pages & Actions" lists
   the FE-owned UI_PAGES registry. A registry entry with no backend row yet is
   lazily materialized on add — created via the normal create-feature endpoint
   and filed straight into the target group (not ui_registry, which is only
   the default home for rows materialized by the Pages & Actions grant panel).

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import { useEffect, useMemo, useState, SyntheticEvent } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Checkbox,
  Chip,
  CircularProgress,
  Typography,
  Tabs,
  Tab,
  Box,
} from '@mui/material';
import { Search } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useGlobalConfig } from 'context/GlobalContext';
import { useToast } from 'context/ToastContext';
import { RbacApis } from 'apis/rbacAPI';
import { selectFeatureGroupsTree } from 'store/dataReducers/rbacSlice';
import { UI_PAGES, UI_PAGE_BY_ID, UI_SECTION_LABELS, UiPageDef, UiSection, isUiEndpoint } from 'framework/access/UiRegistry';
import { Feature, FeatureGroupNode as FeatureGroupNodeType } from 'types/apis/rbacInterfaces';

const chipSx = { height: 18, fontSize: 11, color: 'text.secondary', flexShrink: 0 } as const;

type CategoryTab = 'api' | 'ui';

interface AddFeaturesToGroupDialogProps {
  open: boolean;
  group: { id: number; name: string } | null;
  onClose: () => void;
  onAdded: () => Promise<void> | void;
}

interface UiCandidate {
  endpoint: string;
  label: string;
  kind: 'page' | 'cta';
  pageId: string;
  pageLabel: string;
  section: UiSection;
}

const UI_CANDIDATES: UiCandidate[] = Object.values<UiPageDef>(UI_PAGES).flatMap((page) => [
  { endpoint: page.id, label: page.label, kind: 'page' as const, pageId: page.id, pageLabel: page.label, section: page.section },
  ...Object.values(page.ctas).map((cta) => ({
    endpoint: cta.id,
    label: cta.label,
    kind: 'cta' as const,
    pageId: page.id,
    pageLabel: page.label,
    section: page.section,
  })),
]);

/** The proxypulse API actions nested under a registry page/CTA (UiRegistry `apis`). */
const apisFor = (candidate: UiCandidate): readonly string[] => {
  const page = UI_PAGE_BY_ID.get(candidate.pageId);
  if (!page) return [];
  if (candidate.kind === 'page') return page.apis || [];
  const cta = Object.values(page.ctas).find((c) => c.id === candidate.endpoint);
  return cta?.apis || [];
};

const buildGroupNameMap = (nodes: FeatureGroupNodeType[], map: Map<number, string> = new Map()): Map<number, string> => {
  nodes.forEach((node) => {
    map.set(node.id, node.name);
    buildGroupNameMap(node.children || [], map);
  });
  return map;
};

export const AddFeaturesToGroupDialog = ({ open, group, onClose, onAdded }: AddFeaturesToGroupDialogProps) => {
  const { rc } = useGlobalConfig();
  const toast = useToast();
  const featureGroupsTree = useSelector(selectFeatureGroupsTree);

  const [tab, setTab] = useState<CategoryTab>('api');
  const [features, setFeatures] = useState<Feature[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedApi, setSelectedApi] = useState<Set<number>>(new Set());
  const [selectedUi, setSelectedUi] = useState<Set<string>>(new Set());

  const groupNames = useMemo(() => buildGroupNameMap(featureGroupsTree), [featureGroupsTree]);

  /** Names of every group a feature belongs to, other than the target group — for "in {group}" chips. */
  const otherGroupNames = (ids: number[]): string[] =>
    ids.filter((id) => id !== group?.id).map((id) => groupNames.get(id)).filter((n): n is string => !!n);

  const otherGroupsChipLabel = (ids: number[]): string | undefined => {
    const names = otherGroupNames(ids);
    if (names.length === 0) return undefined;
    if (names.length === 1) return `in ${names[0]}`;
    return `in ${names.length} groups`;
  };

  useEffect(() => {
    if (!open) return;
    setTab('api');
    setSearch('');
    setSelectedApi(new Set());
    setSelectedUi(new Set());
    (async () => {
      setLoading(true);
      const resp = await RbacApis.getFeatures(rc);
      if (resp.success && resp.data) setFeatures(resp.data);
      else if (resp.error) toast.error(resp.error);
      setLoading(false);
    })();
  }, [open]);

  const term = search.trim().toLowerCase();

  /** Backend rows already materialized for registry entries, keyed by ui.* endpoint. */
  const uiByEndpoint = useMemo(
    () => new Map(features.filter((f) => isUiEndpoint(f.endpoint)).map((f) => [f.endpoint, f])),
    [features],
  );

  /** Auto-discovered API features keyed by endpoint — resolves the `apis` nested under registry entries. */
  const apiByEndpoint = useMemo(
    () => new Map(features.filter((f) => !isUiEndpoint(f.endpoint)).map((f) => [f.endpoint, f])),
    [features],
  );

  const apiCandidates = useMemo(
    () =>
      features
        .filter((f) => !isUiEndpoint(f.endpoint))
        .filter((f) => !f.feature_group_ids.includes(group?.id ?? -1))
        .filter(
          (f) =>
            !term ||
            (f.label || '').toLowerCase().includes(term) ||
            f.endpoint.toLowerCase().includes(term) ||
            f.path.toLowerCase().includes(term),
        ),
    [features, group, term],
  );

  const uiCandidates = useMemo(
    () =>
      UI_CANDIDATES
        .filter((c) => !(uiByEndpoint.get(c.endpoint)?.feature_group_ids ?? []).includes(group?.id ?? -1))
        .filter(
          (c) =>
            !term ||
            c.label.toLowerCase().includes(term) ||
            c.pageLabel.toLowerCase().includes(term) ||
            c.endpoint.toLowerCase().includes(term) ||
            apisFor(c).some((api) => api.toLowerCase().includes(term)),
        ),
    [uiByEndpoint, group, term],
  );

  const uiBySection = useMemo(() => {
    const map = new Map<UiSection, UiCandidate[]>();
    uiCandidates.forEach((c) => {
      const list = map.get(c.section) || [];
      list.push(c);
      map.set(c.section, list);
    });
    return map;
  }, [uiCandidates]);

  const toggleApi = (id: number) => {
    setSelectedApi((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /** Ids of synced API features not already in the target group. */
  const selectableApiIds = (endpoints: readonly string[]): number[] =>
    endpoints
      .map((endpoint) => apiByEndpoint.get(endpoint))
      .filter((f): f is Feature => !!f && !f.feature_group_ids.includes(group?.id ?? -1))
      .map((f) => f.id);

  // Selecting a page pre-selects all its actions and their linked API
  // permissions (skipping unsynced ones and ones already in the target
  // group) — deselect individual rows to trim. Selecting a CTA pre-selects
  // its own APIs. Deselecting removes only the row itself.
  const toggleUi = (candidate: UiCandidate) => {
    const selecting = !selectedUi.has(candidate.endpoint);
    setSelectedUi((prev) => {
      const next = new Set(prev);
      if (!selecting) {
        next.delete(candidate.endpoint);
        return next;
      }
      next.add(candidate.endpoint);
      if (candidate.kind === 'page') {
        UI_CANDIDATES
          .filter((c) => c.kind === 'cta' && c.pageId === candidate.pageId)
          .filter((c) => !(uiByEndpoint.get(c.endpoint)?.feature_group_ids ?? []).includes(group?.id ?? -1))
          .forEach((c) => next.add(c.endpoint));
      }
      return next;
    });
    if (selecting) {
      const apiEndpoints = candidate.kind === 'page'
        ? UI_CANDIDATES.filter((c) => c.pageId === candidate.pageId).flatMap((c) => [...apisFor(c)])
        : [...apisFor(candidate)];
      const ids = selectableApiIds(apiEndpoints);
      if (ids.length) setSelectedApi((prev) => new Set([...prev, ...ids]));
    }
  };

  const totalSelected = selectedApi.size + selectedUi.size;
  const uiLabelFor = (endpoint: string) => UI_CANDIDATES.find((c) => c.endpoint === endpoint);

  const handleSubmit = async () => {
    if (!group || totalSelected === 0) return;
    setSubmitting(true);

    const errors: string[] = [];
    const featureIds: number[] = [...selectedApi];

    for (const endpoint of selectedUi) {
      const existing = uiByEndpoint.get(endpoint);
      if (existing) {
        featureIds.push(existing.id);
        continue;
      }
      const candidate = uiLabelFor(endpoint);
      const label = candidate?.kind === 'cta' ? `${candidate.pageLabel} · ${candidate.label}` : candidate?.label;
      const created = await RbacApis.createFeature(rc, {
        endpoint,
        path: `ui://${endpoint}`,
        methods: 'UI',
        domain: 'ui',
        label,
      });
      if (created.success && created.data) featureIds.push(created.data.id);
      else errors.push(label || endpoint);
    }

    const resp = featureIds.length > 0
      ? await RbacApis.addFeaturesToGroup(rc, group.id, featureIds)
      : { success: true as const, error: undefined };

    setSubmitting(false);
    if (!resp.success && resp.error) {
      toast.error(resp.error);
      return;
    }
    if (errors.length) {
      toast.error(`Could not create: ${errors.join(', ')}`);
    } else {
      toast.success(`${featureIds.length} item(s) added to “${group.name}”`);
    }
    await onAdded();
    onClose();
  };

  /**
   * API permission nested under a page/CTA — same underlying feature as the
   * "API Permissions" tab (shared selectedApi set), just surfaced in context.
   */
  const renderApiRow = (api: string, indent: number) => {
    const feature = apiByEndpoint.get(api);
    const synced = !!feature;
    const inGroup = synced && feature.feature_group_ids.includes(group?.id ?? -1);
    const otherGroupsChip = feature ? otherGroupsChipLabel(feature.feature_group_ids) : undefined;
    return (
      <ListItemButton
        key={api}
        onClick={() => feature && !inGroup && toggleApi(feature.id)}
        dense
        disabled={!synced || inGroup}
        sx={{ pl: indent }}
      >
        <Checkbox
          size="small"
          edge="start"
          checked={!!feature && selectedApi.has(feature.id)}
          tabIndex={-1}
          disableRipple
        />
        <Chip size="small" variant="outlined" label="API" sx={{ ...chipSx, mr: 1 }} />
        <ListItemText
          primary={feature?.label || api}
          secondary={api}
          secondaryTypographyProps={{ fontSize: 12, noWrap: true, fontFamily: 'monospace' }}
        />
        {!synced && <Chip size="small" variant="outlined" label="not synced" sx={chipSx} />}
        {inGroup && <Chip size="small" variant="outlined" label="in this group" sx={chipSx} />}
        {otherGroupsChip && <Chip size="small" variant="outlined" label={otherGroupsChip} sx={{ ...chipSx, ml: 0.5 }} />}
      </ListItemButton>
    );
  };

  const renderUiRow = (candidate: UiCandidate) => {
    const existing = uiByEndpoint.get(candidate.endpoint);
    const otherGroupsChip = existing ? otherGroupsChipLabel(existing.feature_group_ids) : undefined;
    return (
      <ListItemButton
        key={candidate.endpoint}
        onClick={() => toggleUi(candidate)}
        dense
        sx={{ pl: candidate.kind === 'cta' ? 4 : 2 }}
      >
        <Checkbox size="small" edge="start" checked={selectedUi.has(candidate.endpoint)} tabIndex={-1} disableRipple />
        <ListItemText
          primary={candidate.label}
          primaryTypographyProps={candidate.kind === 'page' ? { fontWeight: 500 } : undefined}
          secondary={candidate.endpoint}
          secondaryTypographyProps={{ fontSize: 12, noWrap: true, fontFamily: 'monospace' }}
        />
        <Chip size="small" variant="outlined" label={candidate.kind === 'page' ? 'Page' : 'Action'} sx={chipSx} />
        {otherGroupsChip && <Chip size="small" variant="outlined" label={otherGroupsChip} sx={{ ...chipSx, ml: 0.5 }} />}
      </ListItemButton>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0 }}>Add to “{group?.name}”</DialogTitle>
      <Tabs value={tab} onChange={(_: SyntheticEvent, value: CategoryTab) => setTab(value)} sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label={`API Permissions${selectedApi.size ? ` (${selectedApi.size})` : ''}`} value="api" />
        <Tab label={`Pages & Actions${selectedUi.size ? ` (${selectedUi.size})` : ''}`} value="ui" />
      </Tabs>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <TextField
          size="small"
          fullWidth
          autoFocus
          placeholder={tab === 'api' ? 'Search API permissions…' : 'Search pages & actions…'}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mt: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ height: 360, overflowY: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
          {loading && <CircularProgress size={20} sx={{ m: 2 }} />}

          {!loading && tab === 'api' && (
            <>
              {apiCandidates.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  No matching API permissions.
                </Typography>
              )}
              <List dense disablePadding>
                {apiCandidates.map((feature) => {
                  const otherGroupsChip = otherGroupsChipLabel(feature.feature_group_ids);
                  return (
                    <ListItemButton key={feature.id} onClick={() => toggleApi(feature.id)} dense>
                      <Checkbox size="small" edge="start" checked={selectedApi.has(feature.id)} tabIndex={-1} disableRipple />
                      <ListItemText
                        primary={feature.label || feature.endpoint}
                        secondary={feature.path}
                        secondaryTypographyProps={{ fontSize: 12, noWrap: true }}
                      />
                      {otherGroupsChip && <Chip size="small" variant="outlined" label={otherGroupsChip} sx={chipSx} />}
                    </ListItemButton>
                  );
                })}
              </List>
            </>
          )}

          {!loading && tab === 'ui' && (
            <>
              {uiCandidates.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  No matching pages or actions.
                </Typography>
              )}
              <List dense disablePadding>
                {Array.from(uiBySection.entries()).map(([section, candidates]) => (
                  <Box key={section}>
                    <Typography variant="caption" color="text.secondary" sx={{ px: 2, pt: 1, display: 'block', fontWeight: 600 }}>
                      {UI_SECTION_LABELS[section]}
                    </Typography>
                    {candidates.map((candidate) => (
                      <Box key={candidate.endpoint}>
                        {renderUiRow(candidate)}
                        {apisFor(candidate).map((api) => renderApiRow(api, candidate.kind === 'page' ? 4 : 6))}
                      </Box>
                    ))}
                  </Box>
                ))}
              </List>
            </>
          )}
        </Box>
        {totalSelected > 0 && (
          <Typography variant="caption" color="text.secondary">
            {totalSelected} selected.
            {selectedUi.size > 0 && ' Pages/actions without a backend row yet will be created automatically.'}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || totalSelected === 0}
          startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          Add {totalSelected > 0 ? `(${totalSelected})` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
